import React, { useEffect, useState, Fragment } from 'react';
import { Table, Spin, Button, DatePicker, message, Input, Select } from 'antd';
import { getFtaApplicabilityDataService, getFtaApplicabilityDashboardSummaryService } from '../../../services/FTAReportService';
import { getAllAdminIECsService } from '../../../services/FileDataService';
import { getBRCorelationService } from '../../../services/MasterService';
import moment from 'moment';
import { LoadingOutlined } from '@ant-design/icons';
import { Excel } from 'antd-table-saveas-excel';
import { Col, Row } from 'react-bootstrap';
import Card from '../../../components/bootstrap/card';
import { getTodaysDate, formatToTwoDecimals } from '../../../utilities/utils';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import dayjs from 'dayjs';
import { useLocation } from 'react-router-dom';
import ReportDateFilter from '../../../components/ReportDateFilter';

import useColumnVisibility from '../../../hooks/useColumnVisibility';
import ColumnSelector from '../../../components/common/ColumnSelector';

const { RangePicker } = DatePicker;

const FTAApplicabilityReport = () => {
    const [jsonData, setJsonData] = useState([]);
    const [isLoading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [selectedIECName, setSelectedIECName] = useState(localStorage.getItem('name'));
    const [isValid, setIsValid] = useState(true);
    const [billNo, setBillNo] = useState("");
    const [branchCodeNames, setBranchCodeNames] = useState([]);
    const [selectedBranchCodes, setSelectedBranchCodes] = useState([]);
    const [applicableFilter, setApplicableFilter] = useState("All");
    const [remarkFilter, setRemarkFilter] = useState("ALL");
    const [cooCosFilter, setCooCosFilter] = useState("ALL");

    const location = useLocation();
    const currentYear = dayjs().year();
    const defaultFrom = dayjs(`${currentYear}-01-01`);
    const defaultTo = dayjs(`${currentYear}-12-31`);
    const [dates, setDates] = useState(() => {
        if (location.state?.passedDates?.length === 2) {
            return [
                dayjs(location.state.passedDates[0]),
                dayjs(location.state.passedDates[1]),
            ];
        }
        return [defaultFrom, defaultTo];
    });

    const [IECOptions, setIECOptions] = useState([]);
    const userRole = localStorage.getItem('role');
    const [selectedIEC, setSelectedIEC] = useState(
        localStorage.getItem('iec_no')
    );

    // Define columns for the table
    const columns = [
        {
            title: 'Business Unit',
            dataIndex: 'business_unit',
            key: 'business_unit',
            header: 'Business Unit',
            width: 160, fixed: 'left',
        },
        {
            title: 'Plant Name',
            dataIndex: 'plant_name',
            key: 'plant_name',
            header: 'Plant Name',
            width: 150, fixed: 'left',
        },
        {
            title: 'Entity',
            dataIndex: 'entity',
            key: 'entity',
            header: 'Entity',
            width: 150,
        },
        {
            title: 'Org. Code',
            dataIndex: 'org_code',
            key: 'org_code',
            header: 'Org. Code',
            width: 120,
        },
        {
            title: 'IEC/BR',
            dataIndex: 'iec_br',
            key: 'iec_br',
            header: 'IEC/BR',
            width: 120,
        },
        {
            title: 'UID No.',
            dataIndex: 'reference_code',
            key: 'reference_code',
            header: 'UID No.',
            width: 120,
        },
        {
            title: 'SHB No.',
            dataIndex: 'sb_no',
            key: 'sb_no',
            header: 'SHB No.',
            width: 120,
        },
        {
            title: 'SHB Dt.',
            dataIndex: 'sb_date',
            key: 'sb_date',
            header: 'SHB Dt.',
            width: 120,
            render: (text, record) => {
                if (record.sb_date) {
                    return formatDateToDDMMYY(record.sb_date);
                }
            }
        },
        {
            title: 'HSN Code',
            dataIndex: 'hsn_code',
            key: 'hsn_code',
            header: 'HSN Code',
            width: 120,
        },
        {
            title: 'Country of Destination',
            dataIndex: 'country_of_destination',
            key: 'country_of_destination',
            header: 'Country of Destination',
            width: 230,
        },
        {
            title: 'Country Code',
            dataIndex: 'country_code',
            key: 'country_code',
            header: 'Country Code',
            width: 140,
        },
        {
            title: 'PT Abroad',
            dataIndex: 'pt_abroad',
            key: 'pt_abroad',
            header: 'PT Abroad',
            width: 120,
        },
        {
            title: 'Inv. No.',
            dataIndex: 'inv_no',
            key: 'inv_no',
            header: 'Inv. No.',
            width: 120,
        },
        {
            title: 'Inv. Dt.',
            dataIndex: 'inv_no_date',
            key: 'inv_no_date',
            header: 'Inv. Dt.',
            width: 120,
            render: (text) => safeExtractDate(text)
        },
        {
            title: 'Applicable COO (AGREEMENT)',
            dataIndex: 'applicable_coo',
            key: 'applicable_coo',
            header: 'Applicable COO (AGREEMENT)',
            width: 270,
        },
        {
            title: 'Issued COO',
            dataIndex: 'issued_coo',
            key: 'issued_coo',
            header: 'Issued COO',
            width: 150,
        },
        {
            title: 'Issue Date',
            dataIndex: 'coo_issue_date',
            key: 'coo_issue_date',
            header: 'Issue Date',
            width: 130,
            render: (text, record) => {
                if (record.coo_issue_date) {
                    return formatDateToDDMMYY(record.coo_issue_date);
                }
            }
        },
        {
            title: 'EGM No',
            dataIndex: 'egm_no',
            key: 'egm_no',
            header: 'EGM No',
            width: 130,
        },
        {
            title: 'EGM Dtd.',
            dataIndex: 'egm_date',
            key: 'egm_date',
            header: 'EGM Dtd.',
            width: 120,
            render: (text, record) => {
                if (record.egm_date) {
                    return formatDateToDDMMYY(record.egm_date);
                }
            }
        },
        {
            title: 'EGM Remarks',
            dataIndex: 'egm_remarks',
            key: 'egm_remarks',
            header: 'EGM Remarks',
            width: 140,
        }
    ];

    const { visibleKeys, setVisibleKeys, filteredColumns, resetToDefault } =
        useColumnVisibility('FTAApplicabilityReport', columns);

    const fetchData = async () => {
        const filters = {
            from_date: dates?.[0] ? dayjs(dates[0]).format("YYYY-MM-DD") : null,
            to_date: dates?.[1] ? dayjs(dates[1]).format("YYYY-MM-DD") : null,
            bill_no: billNo || null,
            br_code: selectedBranchCodes || null,
            applicability_filter: applicableFilter,
            category_remark: remarkFilter !== "ALL" ? remarkFilter : null,
            coo_cos: cooCosFilter !== "ALL" ? cooCosFilter : null,
        };
        setLoading(true);
        try {
            const response = await getFtaApplicabilityDataService(selectedIEC, filters);
            setJsonData(response);
        } catch (error) {
            setError('Error fetching FTA Applicability data.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (userRole === 'superadmin' || userRole === 'group_head') {
            getAdminIEC();
        }
        if (userRole === 'admin' || userRole === 'user') {
            fetchData();
        }
        if (selectedIEC) {
            getBRCorelationService(selectedIEC).then((response) => {
                setBranchCodeNames(response);
            });
        }
    }, []);

    useEffect(() => {
        if (selectedIEC) {
            getBRCorelationService(selectedIEC).then((response) => {
                setBranchCodeNames(response);
            });
            fetchData();
        }
    }, [selectedIEC]);

    function formatDateToDDMMYY(dateString) {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) {
            return '';
        }
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = String(date.getFullYear()).slice(-2);
        return `${day}/${month}/${year}`;
    }

    const getDateOnly = (dateString) => {
        if (dateString && dateString.length !== 0) {
            const date = new Date(dateString);
            return date;
        }
        else {
            return '';
        }
    }

    const safeExtractDate = (text) => {
        if (!text) return '';
        if (typeof text !== 'string') return text;

        const dateMatch = text.match(/\b(\d{2,4}[\/\-]\d{2}[\/\-]\d{2,4})\b/);
        if (dateMatch) {
            return dateMatch[1];
        }
        return text;
    };

    const getParsedDateForExcel = (text) => {
        const extracted = safeExtractDate(text);
        if (typeof extracted === 'string' && extracted.includes('/')) {
            const parts = extracted.split('/');
            if (parts.length === 3 && parts[2].length === 4) {
                const parsed = new Date(`${parts[2]}-${parts[1]}-${parts[0]}`);
                if (!isNaN(parsed.getTime())) {
                    return parsed;
                }
            }
        }
        return getDateOnly(extracted);
    };

    const handleDateChange = (value) => {
        setDates(value || []);
        if (value && value.length === 2) {
            const from_date = value[0];
            const to_date = value[1];

            if (!from_date || !to_date) {
                setIsValid(false);
                return;
            }

            const oneYearLater = from_date.add(1, 'year');
            if (to_date.isAfter(oneYearLater)) {
                message.error("The selected duration should not exceed 1 year.");
                setIsValid(false);
                return;
            }

            setIsValid(true);
        } else {
            setIsValid(false);
        }
    };

    const getAdminIEC = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await getAllAdminIECsService(token);

            if (response?.data) {
                const options = response.data.map((item, index) => ({
                    value: `${item.iec_no};${item.fullname}`,
                    label: `${item.iec_no} (${item.fullname})`,
                    key: index,
                }));
                setIECOptions(options);
            }
        } catch (err) {
            message.error("Failed to load IEC list");
        }
    };

    const handleSubmit = () => {
        if (!selectedIEC) {
            message.error("Please select an IEC");
            return;
        }

        if (dates.length === 2 && isValid) {
            fetchData();
        } else {
            message.error("Please select a valid date range");
        }
    };

    const handleIECSelection = (val) => {
        if (val) {
            const [iec_no, iec_name] = val.split(";");
            setSelectedIEC(iec_no);
            setSelectedIECName(iec_name);
        } else {
            setSelectedIEC(null);
            setSelectedIECName('');
        }
    };

    const handleBillSearch = (value) => {
        setBillNo(value);
    };

    const handleBranchCodeSelection = (values) => {
        setSelectedBranchCodes(values);
    };

    const exportToExcel = async () => {
        let workbook = new ExcelJS.Workbook();
        let worksheet = workbook.addWorksheet('Data');
        var data = jsonData;

        worksheet.columns = filteredColumns.map(col => ({
            header: col.header || col.title,
            key: col.dataIndex,
            width: 20
        }));

        // Apply styles to the header row
        worksheet.getRow(1).eachCell((cell) => {
            cell.font = { bold: true };
            cell.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: 'FFC000' },
            };
            cell.alignment = { vertical: 'middle' };
        });

        data.forEach((obj) => {
            worksheet.addRow({
                ...obj,
                sb_date: getDateOnly(obj.sb_date),
                inv_no_date: getParsedDateForExcel(obj.inv_no_date),
                coo_issue_date: getDateOnly(obj.coo_issue_date),
                egm_date: getDateOnly(obj.egm_date),
            })
        });

        // Add summary sheet
        try {
            const filters = {
                from_date: dates?.[0] ? dayjs(dates[0]).format('YYYY-MM-DD') : null,
                to_date: dates?.[1] ? dayjs(dates[1]).format('YYYY-MM-DD') : null,
                br_code: selectedBranchCodes || null,
                bill_no: billNo || null,
                category_remark: remarkFilter !== "ALL" ? remarkFilter : null,
                coo_cos: cooCosFilter !== "ALL" ? cooCosFilter : null,
            };
            const summaryResponse = await getFtaApplicabilityDashboardSummaryService(selectedIEC, filters);
            const summaryData = summaryResponse?.['FTA-Applicability']?.dashboard_summary || [];

            const summarySheet = workbook.addWorksheet('Summary');
            summarySheet.columns = [
                { header: 'Business Unit', key: 'business_unit', width: 20 },
                { header: 'Plant Name', key: 'plant_name', width: 20 },
                { header: 'Applicable COO', key: 'applicable_coo_count', width: 18 },
                { header: 'Availed COO', key: 'issued_coo_count', width: 18 },
                { header: 'Missed COO', key: 'missed_coo', width: 18 },
                { header: 'Percentage Missed', key: 'percent', width: 20, style: { numFmt: '0.00%' } },
            ];

            summarySheet.getRow(1).eachCell((cell) => {
                cell.font = { bold: true };
                cell.fill = {
                    type: 'pattern',
                    pattern: 'solid',
                    fgColor: { argb: 'FFC000' },
                };
                cell.alignment = { vertical: 'middle', horizontal: 'center' };
            });

            summaryData.forEach((row) => {
                summarySheet.addRow({
                    business_unit: row.business_unit || '',
                    plant_name: row.plant_name || '',
                    applicable_coo_count: row.applicable_coo_count != null ? parseFloat(row.applicable_coo_count) : 0,
                    issued_coo_count: row.issued_coo_count != null ? parseFloat(row.issued_coo_count) : 0,
                    missed_coo: row.missed_coo != null ? parseFloat(row.missed_coo) : 0,
                    percent: row.percent != null ? parseFloat(row.percent) / 100 : 0,
                });
            });
        } catch (err) {
            console.error('Failed to load dashboard summary for export:', err);
            message.warning('Dashboard Summary sheet could not be loaded — exporting without it.');
        }

        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

        saveAs(blob, `Lucrative's CIP DSS - FTA Applicability Report as on ${getTodaysDate()} of ${selectedIEC}.xlsx`);
    };

    const tableStyles = `
        .fta-applicability-table .ant-table-thead > tr > th {
            white-space: normal !important;
            word-break: break-word !important;
            overflow: visible !important;
            text-overflow: unset !important;
            vertical-align: middle !important;
            line-height: 1.3 !important;
            padding-top: 8px !important;
            padding-bottom: 8px !important;
        }
        .fta-applicability-table .ant-table-tbody > tr > td {
            white-space: nowrap;
        }
        .fta-applicability-table .ant-table-container {
            overflow-x: auto !important;
        }
        .fta-applicability-table .ant-table-header {
            overflow: hidden !important;
        }
        .fta-applicability-table .ant-table-sticky-holder {
            overflow: hidden !important;
        }
    `;

    return (
        <Fragment>
            <style>{tableStyles}</style>
            <Row>
                <Col sm="12">
                    <Card>
                        <Card.Header className="d-flex justify-content-between">
                            <div className="header-title">
                                <h4 className="card-title">Lucrative's CIP DSS - FTA Applicability Report as on {moment().format('DD/MM/YYYY')} of {selectedIECName}</h4>
                                <p><i>Note: Subject to FTA/PTA & DGFT conditions fulfilment.</i></p>
                            </div>
                            <div className="d-flex align-items-center gap-2">
                                <ColumnSelector
                                    allColumns={columns}
                                    visibleKeys={visibleKeys}
                                    setVisibleKeys={setVisibleKeys}
                                    onReset={resetToDefault}
                                />
                                <button
                                    className='btn btn-primary cip-btn-submit'
                                    onClick={exportToExcel}
                                >
                                    Export to Excel
                                </button>
                            </div>
                        </Card.Header>
                        <Card.Body className="d-flex justify-content-start align-items-center gap-2 ps-4">
                            {(userRole === 'superadmin' || userRole === 'group_head') && (
                                <Select
                                    showSearch
                                    placeholder="Select IEC"
                                    onChange={handleIECSelection}
                                    allowClear
                                    options={IECOptions}
                                    style={{ width: '300px' }}
                                />
                            )}
                            <ReportDateFilter dates={dates} onChange={handleDateChange} />
                            <Input
                                placeholder="Enter Bill No"
                                allowClear
                                value={billNo}
                                onChange={(e) => handleBillSearch(e.target.value)}
                                style={{ width: 250 }}
                            />
                            <Select
                                mode="multiple"
                                allowClear
                                style={{ width: 250 }}
                                placeholder="Select Branch Code"
                                onChange={handleBranchCodeSelection}
                                options={branchCodeNames.map((item) => ({
                                    value: item.branch_code,
                                    label: `${item.branch_code} / ${item.plant_name} (${item.business_unit})`,
                                }))}
                            />
                            <Select
                                value={applicableFilter}
                                onChange={(val) => setApplicableFilter(val)}
                                style={{ width: 130 }}
                                options={[
                                    { value: 'All', label: 'All' },
                                    { value: 'Applicable', label: 'Applicable' }
                                ]}
                            />
                            <Select
                                value={remarkFilter}
                                onChange={(val) => setRemarkFilter(val || "ALL")}
                                style={{ width: 200 }}
                                placeholder="Select Remark"
                                options={[
                                    { value: 'ALL', label: 'Remark: ALL' },
                                    { value: 'FTA Applicable and Availed', label: 'Applicable & Availed' },
                                    { value: 'FTA Applicable but Not Availed', label: 'Applicable but Not Availed' },
                                    { value: 'FTA Not Applicable', label: 'Not Applicable' }
                                ]}
                            />
                            <Select
                                value={cooCosFilter}
                                onChange={(val) => setCooCosFilter(val || "ALL")}
                                style={{ width: 160 }}
                                placeholder="Select COO = COS"
                                options={[
                                    { value: 'ALL', label: 'COO = COS: ALL' },
                                    { value: 'TRUE', label: 'COO = COS: TRUE' },
                                    { value: 'FALSE', label: 'COO = COS: FALSE' }
                                ]}
                            />
                            <Button type="primary" className="cip-btn-submit"
                                onClick={handleSubmit}>
                                Submit
                            </Button>
                        </Card.Body>
                        <Card.Body className="px-0 report-page">
                            <Table
                                className="fta-applicability-table"
                                bordered
                                columns={filteredColumns}
                                dataSource={jsonData}
                                loading={isLoading}
                                pagination={{ showTotal: (total) => `Total ${total} records`, showSizeChanger: true }}
                                scroll={{ x: 'max-content' }}
                                
                            />
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </Fragment>
    );
};

export default FTAApplicabilityReport;
